import { useState } from 'react';
import { Button, Card, Col, Image, Input, Radio, Row, Space, App } from 'antd';
import { ConfidenceBadge } from './ConfidenceBadge';
import { WarningsPanel } from './WarningsPanel';
import { StructuredFieldsEditor } from './StructuredFieldsEditor';
import { createTopic } from '@/api/topics';
import { writeStructured } from '@/types/topic';
import type { ExtractedDraft, ExtractionWarning } from '@/types/extractedTopic';

interface Props {
  draft: ExtractedDraft;
  confidence: number;
  warnings: ExtractionWarning[];
  attachmentId?: string;
  flyerPreviewUrl?: string;
  investorOptions: string[];
  onSaved: () => void;
  onDiscard: () => void;
}

/**
 * Two-column layout. Left: flyer image (zoomable via AntD Image). Right:
 * confidence chip, warnings, title input, content markdown textarea,
 * StructuredFieldsEditor, Save-as-draft / Save-and-publish radio + buttons.
 */
export function ReviewForm({
  draft,
  confidence,
  warnings,
  attachmentId,
  flyerPreviewUrl,
  investorOptions,
  onSaved,
  onDiscard,
}: Props) {
  const { message } = App.useApp();
  const [title, setTitle] = useState(draft.title.en ?? '');
  const [contentMd, setContentMd] = useState(draft.content_text.en ?? '');
  const [structured, setStructured] = useState(draft.structured);
  const [status, setStatus] = useState<'draft' | 'active'>('draft');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const details = writeStructured(
        {
          title: { en: title },
          content: { text: { en: contentMd } },
        },
        structured,
      );
      await createTopic({
        type_id: 3, // Hot Program (per Spec 1)
        status,
        details,
        display_sections: [{ id: 5, tag_name: 'Hot Program', sort_index: 1 }],
        attachment_ids: attachmentId ? [attachmentId] : [],
      });
      message.success('Topic saved');
      onSaved();
    } catch (e) {
      message.error(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row gutter={16}>
      <Col span={10}>
        {flyerPreviewUrl ? (
          <Image
            src={flyerPreviewUrl}
            alt="flyer"
            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        ) : (
          <Card>(No flyer image — paste-text mode)</Card>
        )}
      </Col>
      <Col span={14}>
        <Space style={{ marginBottom: 12 }}>
          <ConfidenceBadge value={confidence} />
        </Space>
        <WarningsPanel warnings={warnings} />
        <Card title="Title (English)" size="small" style={{ marginBottom: 12 }}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Card>
        <Card title="Content (Markdown)" size="small" style={{ marginBottom: 12 }}>
          <Input.TextArea
            rows={10}
            value={contentMd}
            onChange={(e) => setContentMd(e.target.value)}
          />
        </Card>
        <StructuredFieldsEditor
          value={structured}
          onChange={setStructured}
          investorOptions={investorOptions}
        />
        <div style={{ marginBottom: 16 }}>
          <Radio.Group value={status} onChange={(e) => setStatus(e.target.value)}>
            <Radio value="draft">Save as Draft</Radio>
            <Radio value="active">Save &amp; Publish</Radio>
          </Radio.Group>
        </div>
        <Space>
          <Button onClick={onDiscard}>Discard</Button>
          <Button type="primary" loading={saving} onClick={save}>
            Save
          </Button>
        </Space>
      </Col>
    </Row>
  );
}
